import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Column,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';

import { UsersTablePagination } from './users-table-pagination.tsx';
import { UsersTableToolbar } from './users-table-toolbar.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';

import { Badge } from '@/components/ui/badge.tsx';
import { UsersTableColumnHeader } from './users-table-column-header.tsx';
import { UsersTableRowActions } from './users-table-row-actions.tsx';
import {
  TotpStatus,
  TUser,
  TUserRoles,
  TUserStatus,
  TUserTotpStatus,
  UserRoles,
  UserStatus,
} from '@/features/users/schemas/users.schema.ts';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx';
import { CSSProperties } from 'react';
import { format } from 'date-fns';
import { calculateDateDifference } from '@/utils/compare-dates.ts';
import { cn } from '@/utils/utils.ts';

export const statuses = UserStatus.map((status) => ({
  label: status,
  value: status,
}));

export const roles = UserRoles.map((role) => ({
  label: role,
  value: role,
}));

export const getLabelStatus = (userStatus: TUserStatus) => {
  if (userStatus === 'Active') {
    return 'green';
  }
  if (userStatus === 'Pending Verification') {
    return 'yellow';
  }
  if (userStatus === 'Suspended') {
    return 'red';
  }
  if (userStatus === 'Rejected') {
    return 'red';
  }
  if (userStatus === 'Deactivated') {
    return 'gray';
  }
  if (userStatus === 'Locked') {
    return 'purple';
  }
  if (userStatus === 'Archived') {
    return 'blue';
  }
  if (userStatus === 'Pending Approval') {
    return 'indigo';
  }
  if (userStatus === 'Expired') {
    return 'pink';
  }
  if (userStatus === 'Maintenance') {
    return 'yellow';
  }
  return 'green';
};

export const getLabelRole = (userRole: TUserRoles) => {
  if (userRole === 'Company owner') {
    return 'pink';
  }
  if (userRole === 'Company administrator') {
    return 'purple';
  }
  if (userRole === 'Manufacturer') {
    return 'green';
  }
  if (userRole === 'Installer') {
    return 'blue';
  }
  if (userRole === 'Owner') {
    return 'yellow';
  }
  if (userRole === 'Guest') {
    return 'gray';
  }
  return 'gray';
};

export function getTotpStatusBadgeVariant(status: (typeof TotpStatus)[number]) {
  switch (status) {
    case 'Enabled':
      return 'green';
    case 'Pending Setup':
      return 'yellow';
    case 'Suspended':
      return 'red';
    case 'Recovery Mode':
      return 'purple';
    case 'Disabled':
      return 'gray';
    default:
      return 'gray';
  }
}

export const columns: ColumnDef<TUser>[] = [
  {
    size: 200,
    accessorKey: 'firstName',
    header: ({ column }) => (
      <UsersTableColumnHeader className="ml-2" column={column} title="Name" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center space-x-2">
          <Avatar className="cursor-pointer">
            <AvatarImage src="" />
            <AvatarFallback className="bg-slate-100">
              {row.original?.firstName?.[0]}
              {row.original?.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <span className="max-w-[500px] text-nowrap">
            {row.original?.firstName} {row.original?.lastName}
          </span>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      return `${rowA.original?.firstName} ${rowA.original?.lastName}`.localeCompare(
        `${rowB.original?.firstName} ${rowB.original?.lastName}`
      );
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => <UsersTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => {
      return (
        <div className="flex space-x-2">
          <span className="max-w-[500px]">{row.getValue('email')}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <UsersTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const status = statuses.find((status) => status.value === row.getValue('status'));

      if (!status) {
        return null;
      }

      return (
        <div className="flex w-[100px] items-center text-nowrap">
          <Badge variant={getLabelStatus(status.label)}>{status.label}</Badge>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
  {
    accessorKey: 'role',
    header: ({ column }) => <UsersTableColumnHeader column={column} title="Role" />,
    cell: ({ row }) => {
      const role = roles.find((role) => role.value === row.getValue('role'));

      if (!role) {
        return null;
      }

      return (
        <div className="flex items-center">
          <Badge variant={getLabelRole(role.label)}>{role.label}</Badge>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
  {
    accessorKey: 'totpStatus',
    header: ({ column }) => (
      <UsersTableColumnHeader className="text-nowrap" column={column} title="2FA Status" />
    ),
    cell: ({ row }) => {
      const totpStatus = (row.getValue('totpStatus') as TUserTotpStatus) || 'Disabled';

      return (
        <div className="flex items-center">
          <Badge variant={getTotpStatusBadgeVariant(totpStatus)}>{totpStatus}</Badge>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
  {
    accessorKey: 'lastAccessTokenAt',
    header: ({ column }) => (
      <UsersTableColumnHeader className="text-nowrap" column={column} title="Last Access" />
    ),
    cell: ({ row }) => {
      let label = '';
      let color = '';

      if (row.getValue('lastAccessTokenAt')) {
        const { days: daysDifference } = calculateDateDifference(
          new Date(row.getValue('lastAccessTokenAt')),
          new Date()
        );

        label = format(new Date(row.getValue('lastAccessTokenAt')), 'MMM dd, yyyy HH:mm');
        color = daysDifference < 7 ? 'text-green-500' : 'text-orange-500';
      }

      return (
        <div className="flex space-x-2 text-nowrap">
          <span className={cn('max-w-[500px] font-medium', color)}>{label}</span>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    size: 10,
    id: 'actions',
    cell: ({ row }) => <UsersTableRowActions row={row} />,
    enablePinning: true,
  },
];

const getCommonPinningStyles = (column: Column<TUser>): CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    opacity: 1,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

export function UsersTable({
  columns,
  data,
  isLoading,
}: {
  columns: ColumnDef<TUser>[];
  data: TUser[];
  isLoading: boolean;
}) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: 20,
      },
      columnPinning: {
        right: ['actions'],
      },
    },
    state: {
      sorting,
      columnFilters,
    },
    enableColumnPinning: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <div className="space-y-4">
      <UsersTableToolbar table={table} />
      <div className="rounded-md border bg-white">
        <div className="relative w-full overflow-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          width: header.getSize() !== 150 ? header.getSize() : undefined,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className="group transition-none duration-0"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ ...getCommonPinningStyles(cell.column) }}
                        className={`px-2 py-3 ${
                          cell.column.id === 'actions' ? 'bg-white group-hover:bg-gray-50' : ''
                        }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {isLoading ? (
                      <div className="flex w-full items-center justify-center py-20">
                        <LoadingSpinner size={50} />
                      </div>
                    ) : (
                      'No results'
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <UsersTablePagination table={table} />
    </div>
  );
}
